import React, { useState, useRef, useLayoutEffect } from 'react';
import ProductLink from '@jetshop/ui/ProductLink';
import { styled } from 'linaria/react';
import Image from '@jetshop/ui/Image';
import { Above } from '@jetshop/ui/Breakpoints';
import { GmfProductRating } from '../CategoryPage/GmfProductRating';
import { getArtNoPartial } from '../ProductPage/ProductPage';
import { cx, css } from 'linaria';
import { ElevateVariants } from './ElevateVariants';
import { checkIsProductGiftCard } from '../../utils/checkIsProductGiftCard';
import { elevateProductToProductLink } from './helpers/elevateProductToProductLink';
import { theme } from '../Theme';
import { AddFavourite } from '../ProductList/AddFavourite';
import { Price } from '../Price';
import { getElevateMemberPrice } from './helpers/getElevateMemberPrice';
import { generateProductStructure } from './generateProductStructure';
import { getPercentage } from '../../utils/getPercentage';
import { useGlobalSettings } from '../Settings/GlobalSettings';
import useVisibilitySettings from '../../utils/useVisibilitySettings';
import { ElevateAvailability } from './ElevateAvailablility';
import { ElevateCardBadge } from './ElevateCardBadge';

const elevateProductCardStyle = css`
  a {
    width: 100%;
    text-decoration: none;
    color: inherit;

    .elevate-product-card-content {
      padding-top: 14px;

      ${theme.below.lg} {
        padding-top: 10px;
      }

      .elevate-product-card-rating-and-availability {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        width: 100%;

        ${theme.below.md} {
          flex-direction: column-reverse;
          align-items: flex-start;
          justify-content: flex-start;
          margin-bottom: 6px;
          .gmf-product-rating {
            min-height: 20px;
          }
        }

        .availability-wrapper {
          margin-top: 6px;

          ${theme.below.md} {
            margin-top: 0;
            margin-bottom: 4px;
          }
        }

        .gmf-product-rating {
          min-height: 20px;
        }
      }

      .elevate-product-card-title {
        font-weight: 500;
        font-size: 14px !important;
        line-height: 1.2 !important;
        min-height: calc(3 * 1.2em) !important;
        overflow: hidden;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 3;
        text-align: left !important;
        text-transform: uppercase;
        color: ${theme.colors.primary};

        margin: 0 !important;
        letter-spacing: 0.02em !important;

        span {
          margin-top: 2px !important;
          font-weight: 500 !important;
          font-size: 11px !important;
          color: ${theme.colors.black};
          text-overflow: ellipsis;
          white-space: nowrap;
          overflow: hidden;
          text-transform: uppercase;
          line-height: 1.2 !important;
          text-align: left !important;
          letter-spacing: 0.06em !important;
        }
      }
    }
  }
`;

const ImageWrapper = styled('div')`
  position: relative;
  &:before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background: rgba(0, 0, 0, 0.03);
    pointer-events: none;
    z-index: 1;
  }
  .main-image,
  .show-on-hover {
    transition: opacity 0.3s ease;
  }

  .show-on-hover {
    opacity: 0;
    position: absolute !important;
    top: 0;
    left: 0;
    right: 0;
  }

  ${theme.above.md} {
    &:hover {
      .show-on-hover {
        opacity: 1;
      }
      .main-image {
        opacity: 0;
      }
    }
  }
`;

export const PriceWrapper = styled('div')`
  display: flex;
  justify-content: flex-start;
  align-items: flex-end;
  font-weight: 700;
  font-size: 3rem;
  text-align: center;

  ${theme.below.sm} {
    font-size: 14px;
    margin-top: 0;
  }

  > div {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    padding-right: 10px;

    --price-font-size: 2.5rem;
    --currency-font-size: 0.8rem;
    --price-font-weight: 600;
    ${theme.below.md} {
      --price-font-size: 2rem;
      --currency-font-size: 0.7rem;
      --price-font-weight: 600;
    }
    @media screen and (max-width: 430px) {
      --price-font-size: 1.4rem;
      --currency-font-size: 0.6rem;
    }

    .percentage {
      align-self: center;
      white-space: nowrap;
      margin: 0 8px;
      color: ${theme.colors.primary};
      background: #e7e1d8;
      font-family: 'Figtree', sans-serif;
      border-radius: 1rem;
      padding: 6px 8px 4px 8px;
      font-size: 0.8rem;
      font-weight: var(--price-font-weight);

      span {
        font-size: var(--currency-font-size);
        font-weight: 800;
      }
      ${theme.below.sm} {
        font-size: 0.7rem;
        padding: 4px 6px 3px 6px;
      }
    }

    .fr {
      font-size: var(--currency-font-size);
      margin-bottom: 4px;
      margin-right: 4px;
    }

    .old-price.fr .fr {
      margin-bottom: 0;
    }

    .new-price,
    .price,
    .member-price {
      display: flex;
      align-items: baseline;

      .currency {
        font-size: var(--currency-font-size);
        text-transform: uppercase;
        margin-left: 4px;
      }
    }

    .new-price {
      color: #b13827;
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
      font-family: 'Figtree', sans-serif;
      font-size: var(--price-font-size);
      font-weight: var(--price-font-weight);
      line-height: 1;
      margin-right: 0;
    }
    .price {
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
      font-family: 'Figtree', sans-serif;
      font-size: var(--price-font-size);
      font-weight: var(--price-font-weight);
      line-height: 1;
      margin-right: 0;
    }
    .old-price {
      color: ${theme.colors.grey};
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
      line-height: 1;
      font-weight: normal;
      display: flex;
      gap: 0.3rem;
      margin-top: 0.2rem;
      display: flex;
      align-items: flex-end;
      padding: 1px 0px;

      font-size: var(--currency-font-size);
      text-decoration: none;
      color: black !important;
      font-weight: bold !important;
      text-transform: uppercase;
      text-decoration: line-through;

      .price-value-and-currency {
        display: flex;
        align-items: flex-end;
        span {
          margin-bottom: 0px !important;
        }
      }
    }
    .member-price {
      color: ${theme.colors.pink};
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
      font-family: 'Figtree', sans-serif;
      font-size: var(--price-font-size);
      font-weight: var(--price-font-weight);
      line-height: 1;
      margin-right: 0;

      .reverse-order {
        gap: 8px;
      }
    }
  }
  &.price-range {
    .price,
    .old-price,
    .new-price {
      display: flex;
      align-items: flex-end;
      &::before {
        display: inline-block;
        content: ${props => props.fromText};
        margin-right: 3px;
        padding-bottom: 5px;
        font-size: 1rem;
        font-family: ${theme.fonts.primary};
      }
    }

    .old-price {
      padding-bottom: 4px;
      &::before {
        padding-bottom: 0px;
      }
    }
  }
`;

const ProductSymbolWrapper = styled('div')`
  position: absolute;
  width: calc(${props => props.symbolSize} + 14px);
  height: calc(${props => props.symbolHeight} + 14px);
  z-index: 2;
  .product-symbol {
    padding-bottom: 0 !important;
    height: calc(${props => props.symbolHeight} + 14px) !important;
    display: content;
    picture {
      display: content;
    }
    img {
      padding: 0px !important;
      max-width: 100% !important;
      max-height: 100% !important;
      width: auto !important;
      height: auto !important;
      position: absolute !important;
    }
  }
  ${theme.below.md} {
    width: calc(${props => props.symbolSizeMobile} + 14px);
    height: calc(${props => props.symbolHeightMobile} + 14px);
    .product-symbol {
      height: calc(${props => props.symbolHeightMobile} + 14px) !important;
      width: calc(${props => props.symbolSizeMobile} + 14px) !important;
    }
  }
`;

const parsePosition = position => {
  switch (position) {
    case 'Överkant - Höger':
      return {
        top: 0,
        right: 0,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'flex-end'
      };
    case 'Överkant - Vänster':
      return {
        top: 0,
        left: 0,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'flex-end'
      };
    case 'Nederkant - Höger':
      return {
        bottom: 0,
        right: 0,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'flex-end'
      };
    case 'Nederkant - Vänster':
      return {
        bottom: 3,
        left: 3,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'flex-end'
      };
    default:
      return {
        top: 0,
        right: 0
      };
  }
};

const useProductTemplate = (product, templates) => {
  const marketingCategories = product?.custom?.marketingCategories?.map(
    category => category.id
  );

  const matchingTemplates = Object.keys(templates)?.filter(category =>
    marketingCategories?.includes(category)
  );

  const productTemplate = templates[matchingTemplates?.[0]] ?? null;

  const countryVisibilitySettings = {
    sweden: productTemplate?.sweden,
    denmark: productTemplate?.denmark,
    norway: productTemplate?.norway,
    finland: productTemplate?.finland,
    netherlands: productTemplate?.netherlands,
    eu: productTemplate?.eu,
    loggedIn: productTemplate?.loggedIn,
    loggedOut: productTemplate?.loggedOut
  };

  const doNotRender = useVisibilitySettings(countryVisibilitySettings);
  const hasHeight = productTemplate?.symbolHeight !== null;

  return {
    productTemplate,
    matchingTemplates,
    countryVisibilitySettings,
    doNotRender,
    hasHeight
  };
};

export const ElevateProductCard = ({
  productGroups,
  as = 'li',
  imageAspect = '1:1',
  imageSizes = [1 / 4, 1 / 3, 1 / 2, 1 / 2],
  index,
  clickCallback = () => {},
  inViewCallback,
  showVariants = true
}) => {
  const [product, setProduct] = useState(productGroups?.products?.[0]);
  const ElevateProductCardWrapper = as;
  const cardRef = useRef(null);
  const { productTemplates, saleTemplate } = useGlobalSettings();
  const { doNotRender, productTemplate, hasHeight } = useProductTemplate(
    product,
    productTemplates
  );

  const isGiftCard = checkIsProductGiftCard(product?.key);
  // const hasStock = checkProductStockLevels(product); // TODO: Check if needs to be adjusted for Elevate
  const hasStockInStore = false; // checkFavoriteStoreStock(product);

  const hasVariantPricing =
    product?.sellingPrice?.min !== product?.sellingPrice?.max;

  const memberPrice = getElevateMemberPrice(product);

  // Register for visibility tracking (observer lives in useProductVisibilityTracking)
  useLayoutEffect(() => {
    if (!inViewCallback || !cardRef.current || !product) return;

    const trackingProduct = generateProductStructure(product);
    if (!trackingProduct) return;

    const productWithIndex = { ...trackingProduct, _gridIndex: index };
    return inViewCallback(cardRef.current, productWithIndex);
  }, [inViewCallback, product, index]);

  return (
    <ElevateProductCardWrapper
      ref={cardRef}
      className={cx('elevate-product-card', elevateProductCardStyle)}
    >
      <ProductLink
        product={elevateProductToProductLink(product)}
        aria-label={product?.title}
        onClick={() =>
          clickCallback(generateProductStructure(product) || product, index)
        }
      >
        <ImageWrapper>
          {productTemplate && productTemplate?.symbol && !doNotRender && (
            <ProductSymbolWrapper
              symbolSize={productTemplate?.symbolSize + 'px'}
              symbolHeight={
                hasHeight
                  ? productTemplate?.symbolHeight + 'px'
                  : productTemplate?.symbolSize + 'px'
              }
              symbolSizeMobile={productTemplate?.symbolSizeMobile + 'px'}
              symbolHeightMobile={productTemplate?.symbolHeightMobile + 'px'}
              style={parsePosition(productTemplate?.symbolPosition)}
            >
              <Image
                className="product-symbol"
                sizes={'100'}
                aspect={'1:1'}
                alt={productTemplate?.symbolText ?? ''}
                title={productTemplate?.symbolText ?? ''}
                src={productTemplate?.symbol}
              />
            </ProductSymbolWrapper>
          )}

          <AddFavourite
            className="add-fav"
            product={{
              articleNumber: product?.key,
              variant: { articleNumber: product?.key }
            }}
          />
          {product?.imageInfo?.images?.length > 0 ? (
            <>
              <ElevateCardBadge product={product} />
              <Image
                className={cx(
                  product.imageInfo.images.length > 1
                    ? 'main-image'
                    : 'only-image'
                )}
                sizes={imageSizes}
                aspect={imageAspect}
                alt={product?.title}
                src={product?.imageInfo?.images?.[0]?.sources?.[0]?.url}
                cover
                critical={true}
              />
              {product.imageInfo.images.length > 1 && (
                <Above breakpoint="md">
                  {() => (
                    <>
                      <ElevateCardBadge product={product} />
                      <Image
                        className="show-on-hover"
                        sizes={imageSizes}
                        aspect={imageAspect}
                        alt={product?.title}
                        src={product?.imageInfo?.images?.[1]?.sources?.[0]?.url}
                        cover
                      />
                    </>
                  )}
                </Above>
              )}
            </>
          ) : (
            <Image aspect={imageAspect} />
          )}
        </ImageWrapper>
        <div className="elevate-product-card-content">
          <div className="elevate-product-card-rating-and-availability">
            <GmfProductRating productId={getArtNoPartial(product?.key)} />

            <ElevateAvailability product={product} />
          </div>
          <header style={{ flexDirection: 'column' }}>
            <h2
              className={'elevate-product-card-title'}
              dangerouslySetInnerHTML={{
                __html: `<span class="sub-name">${
                  product?.brand ?? ''
                }&nbsp;</span><br />${product?.title}`
              }}
            />
          </header>
          <PriceWrapper
            fromText={'Fr.'}
            className={hasVariantPricing ? 'price-range' : 'single-price'}
          >
            <Price
              memberPrice={{ incVat: memberPrice }}
              price={{ incVat: product?.sellingPrice?.min }}
              previousPrice={{ incVat: product?.listPrice?.min }}
              fromPrice={hasVariantPricing ? product?.sellingPrice?.min : null}
              fixedPercentage={getPercentage(product, memberPrice)}
            />
          </PriceWrapper>
        </div>
        {showVariants && (
          <ElevateVariants
            variantProducts={productGroups?.products}
            selectedVariantKey={product?.key}
            onVariantChange={variant => setProduct(variant)}
          />
        )}
      </ProductLink>
    </ElevateProductCardWrapper>
  );
};
